/**
 * Magnifying lens hover interaction for the product media gallery.
 *
 * Each eligible `.product-media-container` gets a circular lens overlay that
 * follows the cursor and shows a magnified view of the area beneath it. The
 * lens source is the 3840-wide image URL already exposed via `data-max-resolution`
 * on the gallery's <img> element.
 *
 * Skipped on touch-primary devices via the `(hover: hover) and (pointer: fine)`
 * CSS media query and a matchMedia guard here.
 */

const SELECTOR =
  '.media-gallery__grid .product-media-container--zoomable.product-media-container--image';

const isHoverCapable = () =>
  window.matchMedia('(hover: hover) and (pointer: fine)').matches;

class MagnifyingLensController {
  constructor(container) {
    this.container = container;
    this.img = container.querySelector('img.product-media__image');
    if (!this.img) return;

    this.zoom = parseFloat(container.dataset.lensZoom || '2');

    this.lens = document.createElement('div');
    this.lens.className = 'magnifying-lens';
    this.lens.setAttribute('aria-hidden', 'true');
    container.appendChild(this.lens);

    this.onEnter = this.onEnter.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onLeave = this.onLeave.bind(this);

    container.addEventListener('mouseenter', this.onEnter);
    container.addEventListener('mousemove', this.onMove);
    container.addEventListener('mouseleave', this.onLeave);
  }

  resolveSource() {
    return (
      this.img.dataset.maxResolution ||
      this.img.currentSrc ||
      this.img.src ||
      ''
    );
  }

  onEnter() {
    const src = this.resolveSource();
    if (!src) return;
    if (this.lens.dataset.src !== src) {
      this.lens.style.backgroundImage = `url("${src}")`;
      this.lens.dataset.src = src;
    }
    this.lens.classList.add('is-active');
  }

  onMove(event) {
    const rect = this.container.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
      this.onLeave();
      return;
    }

    const lensSize = this.lens.offsetWidth || 180;
    const half = lensSize / 2;

    // Clamp the lens fully inside the container so it never bleeds out the side.
    const lx = Math.max(0, Math.min(rect.width - lensSize, x - half));
    const ly = Math.max(0, Math.min(rect.height - lensSize, y - half));

    // The background tile is the image scaled up by the zoom factor.
    const bgW = rect.width * this.zoom;
    const bgH = rect.height * this.zoom;

    // Position so the cursor's image-space point lands at the lens center.
    const bgX = -((x / rect.width) * bgW - half);
    const bgY = -((y / rect.height) * bgH - half);

    this.lens.style.transform = `translate(${lx}px, ${ly}px)`;
    this.lens.style.backgroundSize = `${bgW}px ${bgH}px`;
    this.lens.style.backgroundPosition = `${bgX}px ${bgY}px`;
  }

  onLeave() {
    this.lens.classList.remove('is-active');
  }
}

const init = () => {
  if (!isHoverCapable()) return;
  document.querySelectorAll(SELECTOR).forEach((el) => {
    if (el._magnifyingLens) return;
    el._magnifyingLens = new MagnifyingLensController(el);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// Re-init when Shopify section editor swaps content or variant change re-renders media.
const observer = new MutationObserver(() => init());
observer.observe(document.body, { childList: true, subtree: true });

document.addEventListener('shopify:section:load', init);
document.addEventListener('shopify:block:select', init);
